<template>
  <div
    ref="rootEl"
    class="relative inline-block text-left font-sans"
  >
    <!-- Trigger / Field  -->
    <button
      type="button"
      class="w-44 select-none inline-flex items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left text-sm text-gray-900 shadow-sm transition-colors hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-200"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-haspopup="dialog"
      @click="toggle()"
      @keydown.prevent.space="toggle()"
      @keydown.enter.prevent="toggle()"
      @keydown.arrow-up.prevent="nudge(1)"
      @keydown.arrow-down.prevent="nudge(-1)"
    >
      <span
        class="truncate"
        :class="!displayValue ? 'text-gray-600' : ''"
      >
        {{ displayValue || placeholder }}
      </span>

      <!-- Calendar icon  -->
      <svg
        class="ml-2 h-4 w-4 text-gray-600"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        aria-hidden="true"
      >
        <rect
          x="3"
          y="4"
          width="18"
          height="18"
          rx="2"
          ry="2"
          stroke-width="1.5"
        ></rect>
        <line
          x1="16"
          y1="2.5"
          x2="16"
          y2="6.5"
          stroke-width="1.5"
        ></line>
        <line
          x1="8"
          y1="2.5"
          x2="8"
          y2="6.5"
          stroke-width="1.5"
        ></line>
        <line
          x1="3"
          y1="10"
          x2="21"
          y2="10"
          stroke-width="1.5"
        ></line>
      </svg>
    </button>

    <!-- Popover  -->
    <div
      v-if="isOpen"
      class="absolute z-50 mt-2 w-64 rounded-xl border border-gray-200 bg-white p-3 shadow-lg"
      role="dialog"
      aria-label="Select year"
      @keydown.esc.stop.prevent="close()"
    >
      <!-- Header / Decade navigation  -->
      <div class="mb-2 flex items-center justify-between">
        <button
          type="button"
          class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-900 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-200"
          @click="prevDecade"
          aria-label="Previous decade"
        >
          <svg
            class="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              d="M15 18l-6-6 6-6"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <div class="text-sm font-medium text-gray-900">
          {{ decadeLabel }}
        </div>

        <button
          type="button"
          class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-900 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-200"
          @click="nextDecade"
          aria-label="Next decade"
        >
          <svg
            class="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              d="M9 6l6 6-6 6"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <!-- Years grid  -->
      <div class="grid grid-cols-4 gap-2">
        <button
          v-for="y in years"
          :key="y"
          type="button"
          class="h-9 rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200"
          :class="yearClasses(y)"
          :disabled="isDisabled(y)"
          @click="selectYear(y)"
        >
          {{ y }}
        </button>
      </div>

      <!-- Footer: quick actions  -->
      <div class="mt-3 flex items-center justify-between">
        <!-- <button
          type="button"
          class="text-sm text-blue-600 hover:underline disabled:opacity-50"
          @click="goToCurrentDecade"
        >
          Current decade
        </button> -->
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-900 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-200"
            @click="clearSelection"
          >
            Clear
          </button>
          <button
            type="button"
            class="rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
            @click="close()"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import {
    ref,
    computed,
    watch,
    onMounted,
    onBeforeUnmount,
    nextTick,
  } from "vue";

  const props = defineProps({
    modelValue: {
      type: [Number, String, null],
      default: null,
    },
    minYear: {
      type: Number,
      default: 1970,
    },
    maxYear: {
      type: Number,
      default: () => new Date().getFullYear() + 10,
    },
    placeholder: {
      type: String,
      default: "Select year",
    },
  });

  const emit = defineEmits(["update:modelValue", "selected-year"]);

  const rootEl = ref(null);
  const isOpen = ref(false);

  const currentYear = new Date().getFullYear();

  // Internal selected year (as number | null)
  const selected = ref(
    props.modelValue !== null && props.modelValue !== ""
      ? Number(props.modelValue)
      : null
  );

  // View state: which decade is being shown
  const viewYear = ref(selected.value || currentYear);
  const startOfDecade = computed(() => Math.floor(viewYear.value / 10) * 10);

  // We show 12 cells: previous year, the whole decade, and next year for context
  const years = computed(() => {
    const start = startOfDecade.value - 1;
    return Array.from({ length: 12 }, (_, i) => start + i);
  });

  const decadeLabel = computed(() => {
    const from = startOfDecade.value;
    const to = startOfDecade.value + 9;
    return `${from}–${to}`;
  });

  const displayValue = computed(() =>
    selected.value ? String(selected.value) : ""
  );

  watch(
    () => props.modelValue,
    (val) => {
      selected.value = val !== null && val !== "" ? Number(val) : null;
      if (selected.value) viewYear.value = selected.value;
    }
  );

  function isDisabled(y) {
    return y < props.minYear || y > props.maxYear;
  }

  function yearClasses(y) {
    const isSelected = selected.value === y;
    const isCurrent = y === currentYear;
    const disabled = isDisabled(y);

    return [
      "border",
      "border-gray-200",
      "text-gray-900",
      "hover:border-gray-300",
      disabled && "opacity-40 cursor-not-allowed hover:border-gray-200",
      isCurrent && !isSelected && "ring-1 ring-blue-600/30",
      isSelected &&
        "bg-blue-600 text-white border-blue-600 hover:border-blue-600",
    ]
      .filter(Boolean)
      .join(" ");
  }

  function selectYear(y) {
    if (isDisabled(y)) return;
    selected.value = y;
    emit("update:modelValue", y);
    emit("selected-year", y);
    // close();
  }

  function prevDecade() {
    viewYear.value = startOfDecade.value - 10;
  }

  function nextDecade() {
    viewYear.value = startOfDecade.value + 10;
  }

  function clearSelection() {
    selected.value = null;
    emit("update:modelValue", null);
    emit("selected-year", null);
  }

  function open() {
    if (isOpen.value) return;
    isOpen.value = true;
    // Align decade to current selection for better context
    if (selected.value) viewYear.value = selected.value;
    nextTick(() => {
      // could focus first enabled year if desired
    });
  }

  function close() {
    isOpen.value = false;
  }

  function toggle() {
    isOpen.value ? close() : open();
  }

  // Keyboard increment/decrement from the closed field
  function nudge(delta) {
    const base =
      selected.value ?? clamp(currentYear, props.minYear, props.maxYear);
    const next = clamp(base + delta, props.minYear, props.maxYear);
    selected.value = next;
    emit("update:modelValue", next);
    emit("selected-year", next);
    viewYear.value = next;
  }

  function clamp(n, min, max) {
    return Math.min(Math.max(n, min), max);
  }

  // Click outside to close
  function handleClickOutside(e) {
    const el = rootEl.value;
    if (!el) return;
    if (isOpen.value && !el.contains(e.target)) {
      close();
    }
  }

  onMounted(() => {
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, {
      passive: true,
    });
  });
  onBeforeUnmount(() => {
    document.removeEventListener("mousedown", handleClickOutside);
    document.removeEventListener("touchstart", handleClickOutside);
  });
</script>
